import { Clear, FileUpload } from "@mui/icons-material"
import { Box, BoxProps, Chip, IconButton, Stack, Tooltip, Typography, useTheme } from "@mui/material"
import { grey, lightBlue } from "@mui/material/colors"
import { useCallback, useState } from "react"
import { ErrorCode, FileError, FileRejection, useDropzone } from "react-dropzone"
import { getFileExtension, getUniqueFileExtensions } from "../utils/FileUtils"
import { NexAlert } from "./Alert/Alert"

const lightBackgroundColor = grey[50]

interface NexDropzoneProps extends Omit<BoxProps, "onChange"> {
    onChange: (files: File[]) => void
    files?: File[]
    acceptedFileTypes?: string[]
    filesLimit?: number
    maxFileSize?: number
    requireSameFileType?: boolean
}

export const NexDropzone = ({
    onChange,
    files,
    acceptedFileTypes,
    filesLimit = 1,
    maxFileSize,
    requireSameFileType = false,
}: NexDropzoneProps) => {
    const theme = useTheme()
    const [innerFiles, setInnerFiles] = useState<File[]>([])
    const filesSelected = files ?? innerFiles
    const [errorMessages, setErrorMessages] = useState<string[]>([])

    const validator = useCallback(
        (file: File | DataTransferItem): FileError | null => {
            if (file && !(file instanceof DataTransferItem)) {
                const ext = getFileExtension(file.name)
                if (!ext) {
                    return {
                        code: "file-invalid-extension",
                        message: "File does not have a valid extension. Check file name is valid.",
                    }
                }

                const acceptedExtensionsToLowerCase = acceptedFileTypes?.map((file) => file.toLowerCase())
                if (acceptedExtensionsToLowerCase && !acceptedExtensionsToLowerCase.includes(ext)) {
                    return {
                        code: ErrorCode.FileInvalidType,
                        message: `File type "${ext}" is not an accepted file type. Accepted types: ${acceptedExtensionsToLowerCase.join(", ")}`,
                    }
                }
            }

            return null
        },
        [acceptedFileTypes],
    )

    const onDrop = (acceptedFiles: File[], fileRejections: FileRejection[]) => {
        // Set error messages from file rejections
        const newErrorMessages = fileRejections.flatMap((reject) => reject.errors.map((error) => error.message))
        let validFiles: File[] = filesSelected

        if (filesLimit) {
            // Allow only the max number of files
            validFiles = [...filesSelected, ...acceptedFiles].slice(0, filesLimit)
        }
        if (filesLimit === 1 && filesSelected.length === 1 && acceptedFiles.length > 0) {
            // file already exists
            newErrorMessages.push(`File limit (${filesLimit}) already reached.`)
        }

        const fileExtensions = getUniqueFileExtensions(validFiles)
        if (requireSameFileType && fileExtensions.length > 1) {
            newErrorMessages.push(
                `Uploaded files are not of the same type: ${fileExtensions.join(", ")}. Make sure all selected file types are the same.`,
            )
            // don't save
        }

        setErrorMessages(newErrorMessages)
        setInnerFiles(validFiles)
        onChange(validFiles)
    }

    const handleDeleteFile = (index: number) => {
        const newFiles = [...filesSelected.slice(0, index), ...filesSelected.slice(index + 1)]
        setInnerFiles(newFiles)
        onChange(newFiles)
    }

    const handleResetErrors = () => {
        setErrorMessages([])
    }

    const handleRemoveAllFiles = () => {
        setInnerFiles([])
        onChange([])
    }

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        maxSize: maxFileSize ?? 1e9,
        validator,
        maxFiles: filesLimit,
        onFileDialogOpen: handleResetErrors,
        onDragEnter: handleResetErrors,
    })

    /**
     *  Custom file accept string for getInputProps()
     *  - restricts file dialog to accept only certain extensions, while avoiding Mime Type warnings
     *  - using the useDropzone() accept attribute requires every file type to be a common Mime type, however it does not
     *    restrict to specific file extensions and instead accepts all file types connected to that Mime type.
     */
    const acceptedFileTypeString = acceptedFileTypes?.join(",")

    const color = theme.palette.primary.main
    return (
        <Stack spacing={2}>
            <Box
                sx={{
                    color,
                    border: `${color} dashed 2px`,
                    borderRadius: 4,
                    background: isDragActive
                        ? `linear-gradient(to right bottom, ${lightBackgroundColor}, ${lightBlue[200]})`
                        : lightBackgroundColor,
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    cursor: "pointer",
                    height: 250,
                    backgroundColor: (theme) => theme.palette.background.paper,
                }}
                {...getRootProps()}
            >
                <input {...getInputProps({ accept: acceptedFileTypeString })} />
                <Typography>
                    <FileUpload sx={{ fontSize: { sm: 40, md: 42 } }} />
                </Typography>
                <Typography sx={{ color, fontSize: { sm: 20, md: 24 } }}>
                    Drag and drop a file <br /> or click here
                </Typography>
            </Box>

            <Typography>
                Selected file(s):
                {filesLimit > 1 && (
                    <Tooltip title={"Clear All Files"} onClick={handleRemoveAllFiles}>
                        <IconButton size="small" disabled={filesSelected.length === 0}>
                            <Clear />
                        </IconButton>
                    </Tooltip>
                )}
            </Typography>

            {filesSelected.length > 0 && (
                <Box>
                    {filesSelected.map((file, idx) => (
                        <Chip
                            sx={{
                                m: 0.5,
                                minWidth: 160,
                                maxWidth: 340,
                                backgroundColor: (theme) => theme.palette.background.paper,
                            }}
                            key={idx}
                            label={file.name}
                            variant="outlined"
                            onDelete={() => handleDeleteFile(idx)}
                        />
                    ))}
                </Box>
            )}

            {errorMessages.length > 0 && (
                <NexAlert disableGutter={true}>
                    {errorMessages.map((error, idx) => (
                        <li key={idx}>{error}</li>
                    ))}
                </NexAlert>
            )}
        </Stack>
    )
}
